import { Component } from '@angular/core';
import {
  AbstractControlOptions,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { IResetPassword } from 'src/app/_types/Auth/auth.types';
import { IError, IResponse } from 'src/app/_types/common.types';
import { AuthService } from 'src/app/services/auth.service';
import { FormService } from 'src/app/shared/services/form.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { NotifierService } from 'src/app/shared/services/notifier.service';
import { matchPasswordValidator } from 'src/app/validators/match_password';
import {
  formNoWhitespaceValidator,
  patterns,
} from 'src/app/validators/pattern_validation';

@Component({
  selector: 'app-forget-reset-email',
  templateUrl: './forget-reset-email.component.html',
  styleUrls: ['./forget-reset-email.component.scss'],
})
export class ForgetResetEmailComponent {
  isAdmin: boolean = false;
  pathName: string = '';
  forgetPasswordFrom: FormGroup;
  resetPassword: FormGroup;
  currentRoute: string = '';
  private unsubscribe$ = new Subject<void>();
  password = false;
  confirmPassword = false;
  loading: boolean = false;

  constructor(
    private activeRoute: ActivatedRoute,
    public router: Router,
    private _FormBuilder: FormBuilder,
    private _AuthService: AuthService,
    public route: ActivatedRoute,
    private _NotifierService: NotifierService,
    private _LoaderService: LoaderService,
    private formService: FormService
  ) {
    //Form builder for forgot password
    this.forgetPasswordFrom = this._FormBuilder.group(
      {
        email: [
          '',
          [Validators.pattern(patterns.EMAIL.regex), Validators.required],
        ],
      },
      { validators: [formNoWhitespaceValidator] } as AbstractControlOptions
    );

    //Form builder for reset password
    this.resetPassword = this._FormBuilder.group(
      {
        password: ['', [Validators.required]],
        confirm_password: [
          '',
          [Validators.required, matchPasswordValidator('password')],
        ],
      },
      { validators: [formNoWhitespaceValidator] } as AbstractControlOptions
    );

    // Subscribe to password changes
    this.resetPassword.get('password')?.valueChanges.subscribe(() => {
      this.resetPassword.get('confirm_password')?.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.pathName = this.activeRoute.snapshot.routeConfig?.path || '';
    this.isAdmin = this.pathName === 'forgot-password/admin';

    if (this.pathName === 'email-verification/:id') {
      this._LoaderService.setState(true);
      this.emailVerifyMail();
    }
  }

  //Forgot password method
  forgotPassword() {
    // this.router.navigate(['/reset-password']);
    this.currentRoute = this.router.url;
    if (this.forgetPasswordFrom.valid) {
      this.loading = true;
      const data = { ...this.forgetPasswordFrom.value };
      this._AuthService.forgetPassword(data, this.isAdmin).subscribe({
        next: (response: IResponse) => {
          if (response.success === true) {
            this.loading = false;
            this._NotifierService.showSuccess(response.message);
            this.router.navigate([this.isAdmin ? '/admin-login' : '/login']);
          }
        },
        error: (error: IError) => {
          this.loading = false;
          console.log(
            'forget password inside error======>',
            error.error?.message
          );
          this._NotifierService.showError(
            error?.message ?? error.error?.message
          );
        },
      });
    }
  }

  //Reset password method
  reset() {
    const id = this.route.snapshot.paramMap.get('id')
      ? this.route.snapshot.paramMap.get('id')
      : undefined;
    if (this.resetPassword.valid && id) {
      this.loading = true;
      const data: IResetPassword = {
        token: id!,
        password: this.resetPassword.value.password,
        confirmPassword: this.resetPassword.value.confirm_password,
      };
      this._AuthService
        .resetPassword(data)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (response: IResponse) => {
            if (response.success) {
              this.loading = false;
              this._NotifierService.showSuccess(response.message);
              this.router.navigate([
                response?.data?.['isAdmin'] ? '/admin-login' : '/login',
              ]);
            }
          },
          error: (error: IError) => {
            this.loading = false;
            console.log('reset password inside error======>', error);
            this._NotifierService.showError(
              error?.message ?? error.error?.message
            );
          },
        });
    }
  }

  //Email verification method
  emailVerifyMail() {
    const id = this.route.snapshot.paramMap.get('id')
      ? this.route.snapshot.paramMap.get('id')
      : undefined;
    if (id) {
      this._AuthService
        .emailVerification(id)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (response: IResponse) => {
            if (response.success) {
              this._LoaderService.setState(false);
              this._NotifierService.showSuccess(response.message);
              this.router.navigate(['/login']);
            }
          },
          error: (error: any) => {
            this._NotifierService.showError(
              error ? error?.message : error.error?.message
            );
            this.router.navigate(['/login']);
          },
        });
    }
  }

  getErrorMessage(
    controlName: string,
    formType: 'forgetPassword' | 'resetPassword'
  ): string {
    const form =
      formType === 'forgetPassword'
        ? this.forgetPasswordFrom
        : this.resetPassword;
    return this.formService.getErrorMessage(controlName, form);
  }

  backToLogin() {
    if (this.isAdmin) {
      this.router.navigate(['/admin-login']);
    } else {
      this.router.navigate(['/login']);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
